import React, { useEffect, useState } from 'react'
import { Button } from '@nalezhytyi/saldo-ui'
import {
  CardElement,
  Elements,
  PaymentRequestButtonElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js'
import { loadStripe, PaymentRequest } from '@stripe/stripe-js'
import { observer } from 'mobx-react'
import settingsStore from 'store/mobx/SettingsStore'

import css from './styles.module.scss'

interface IProps {
  setIsLoading: (isLoading: boolean) => void
  actionEnabled?: boolean
  onSave: (token?: string) => Promise<any>
  disabled?: boolean
  isLegalFlow?: boolean
}

export const CardForm = (props: IProps) => {
  const pkTest = process.env.REACT_APP_STRIPE_PUBLIC_KEY || ''

  const stripePromise = loadStripe(pkTest)

  return (
    <Elements key={'en'} stripe={stripePromise} options={{ locale: 'en' }}>
      <SubscribeForm {...props} />
    </Elements>
  )
}

const SubscribeForm = observer(
  ({ onSave, setIsLoading, actionEnabled, disabled, isLegalFlow }: IProps) => {
    const stripe = useStripe()
    const elements = useElements()
    const { currentStripePlan } = settingsStore
    const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(
      null
    )

    useEffect(() => {
      if (!stripe || isLegalFlow) return

      const pr = stripe.paymentRequest({
        country: 'US',
        currency: 'usd',
        total: {
          label: 'Invoice Maker Pro',
          amount: Math.round(currentStripePlan.price * 100), // Convert to cents
        },
        requestPayerName: true,
        requestPayerEmail: true,
      })

      // Check if Payment Request is available (Apple Pay, Google Pay, etc.)
      pr.canMakePayment().then((result) => {
        if (result) {
          setPaymentRequest(pr)
        }
      })

      pr.on('token', async (ev) => {
        setIsLoading(true)
        await onSave(ev.token.id)
        ev.complete('success')
      })
    }, [stripe, currentStripePlan.price, isLegalFlow])

    const handleSubmit = async () => {
      setIsLoading(true)
      const cardEl = elements?.getElement(CardElement)
      if (stripe && cardEl) {
        const result = await stripe.createToken(cardEl)
        await onSave(result?.token?.id)
      } else {
        setIsLoading(false)
      }
    }

    return (
      <>
        {paymentRequest && !isLegalFlow && (
          <>
            <div className={css.paymentRequestButton}>
              <PaymentRequestButtonElement options={{ paymentRequest }} />
            </div>

            <div className={css.divider}>
              <span>or pay with card</span>
            </div>
          </>
        )}

        <div className={css.inputLabel}>Payment Information</div>
        <div className={css.stripeFormWrap}>
          <CardElement
            onReady={(element) => element.focus()}
            className={css.stripeInput}
          />
        </div>
        <Button
          loading={!stripe || !actionEnabled}
          disabled={!stripe || !actionEnabled || disabled}
          appearance="primary"
          size="md"
          block
          className={css.paywallButton}
          onClick={handleSubmit}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z"></path>
          </svg>
          Continue - ${currentStripePlan.price}
        </Button>
      </>
    )
  }
)
